<template>
  <div class="bl-session">
    <div v-if="!pageLoading">
      <div v-if="session" class="bl-session-header">
        <div
          id="session-header"
          class="flex flex-wrap items-center justify-between flex-shrink-0 h-12 px-6 lg:flex-no-wrap gap-y-2 gap-x-16 bg-pure-white"
        >
          <div class="flex items-center gap-2 truncate">
            <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
            <div
              class="flex items-center flex-shrink-0 h-6 gap-2 px-2 rounded-full bg-success-100"
            >
              <BlIcon
                icon="update"
                color="success"
                class="w-4 h-4"
                :class="{ 'animate-spin': saveLoading }"
              />
              <BlTextBody2
                v-if="saveLoading"
                class="text-success"
                text="Saving changes"
              />
              <BlTextBody2
                v-else
                class="text-success"
                text="All changes saved"
              />
            </div>
          </div>
          <div
            class="h-7.5 flex-grow flex-shrink-0 flex items-center justify-end gap-2"
          >
            <div id="appointment-date" class="flex items-center gap-2">
              <BlIcon icon="calendar-blank" class="w-6 h-6" />
              <div
                v-if="sessionAppointment.timeslot"
                class="flex items-center flex-shrink-0 h-8 px-2 truncate border rounded bg-slate-200 border-slate-200 max-w-64"
              >
                <BlTextBody2
                  class="truncate"
                  :text="sessionAppointment.timeslot"
                />
              </div>
            </div>
            <div id="appointment-supervisors" class="flex items-center gap-2">
              <BlIcon icon="people-therapist" class="w-6 h-6" />
              <div v-if="!appointmentSupervisors.length" class="flex lg:block">
                <div
                  class="flex items-center flex-shrink-0 h-8 px-2 truncate border rounded bg-success-100 border-success-100 max-w-64"
                >
                  <BlTextBody2
                    class="truncate text-success"
                    text="No therapist assigned"
                  />
                </div>
              </div>
              <div
                v-else
                v-for="supervisor in appointmentSupervisors.slice(0, 3)"
                :key="supervisor.id"
                class="flex lg:block"
              >
                <div
                  v-if="supervisor.name"
                  class="flex items-center flex-shrink-0 h-8 px-2 truncate border rounded bg-success-100 border-success-100 max-w-64"
                  :title="supervisor.name"
                >
                  <BlTextBody2
                    class="truncate text-success"
                    :text="supervisor.name"
                  />
                </div>
              </div>
              <div
                class="relative"
                v-if="appointmentSupervisors.slice(3).length"
              >
                <div
                  class="flex items-center justify-center cursor-pointer"
                  @click="openMoreSupervisor = !openMoreSupervisor"
                >
                  <BlBubbleCount
                    :count="`+ ${appointmentSupervisors.slice(3).length}`"
                    class="h-7 hover:bg-light-purple-100"
                  />
                </div>
                <div
                  v-if="openMoreSupervisor"
                  class="absolute top-0 right-0 z-50 flex flex-col gap-1 p-1 mt-10 truncate border rounded bg-pure-white border-grey-200 min-w-40"
                >
                  <div
                    v-for="supervisor in appointmentSupervisors.slice(3)"
                    :key="supervisor.id"
                    class="flex max-w-64"
                  >
                    <div
                      v-if="supervisor.name"
                      class="px-4 py-2 text-left truncate rounded outline-none"
                      :title="supervisor.name"
                    >
                      <BlTextBody2
                        class="truncate text-grey"
                        :text="supervisor.name"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!enableAppointment">
              <BlInputButton
                v-if="!sessionAppointment.user"
                @clicked="newScheduleOpened = true"
                type="outlined"
                dense
                icon="edit"
                :label="'Schedule this session'"
              />
              <BlInputButton
                v-if="sessionAppointment.user"
                @clicked="editScheduleOpened = true"
                type="outlined"
                dense
                icon="edit"
                :label="'Edit'"
              />
            </div>
          </div>
        </div>
        <div class="bl-session-header-detail">
          <div class="bl-session-header-detail-container">
            <input
              type="text"
              placeholder="Type session name"
              class="w-full h-10 px-4 text-2xl font-bold border rounded outline-none text-grey focus:border-light-purple"
              :style="{ lineHeight: 'calc(11 / 7)', letterSpacing: '0.5px' }"
              v-model="sessionName"
              @input="onInputName"
            />
          </div>
          <div class="bl-session-header-action">
            <BlInputButton
              :label="'Duplicate draft from'"
              type="plain"
              @clicked="duplicateSessionOpened = true"
            />
            <BlInputButton
              :label="'Download as PDF'"
              type="plain"
              @clicked="downloadOpened = true"
            />
            <BlInputButton
              label="Start session"
              :loading="launchLoading"
              :disabled="session.status !== 'draft'"
              @clicked="onStartSession"
            />
          </div>
        </div>
      </div>

      <TargetJobProgression />

      <div
        class="flex flex-shrink-0 w-full h-12 gap-4 px-6 pt-2 border-b bg-pure-white border-slate-400"
      >
        <router-link
          v-for="tab in tabs"
          :key="tab.target"
          :to="tab.target"
          class="flex items-center justify-center w-full transition-all duration-300 border-b-4"
          :class="tab.isActive ? 'border-light-purple' : 'border-pure-white'"
        >
          <BlTextSubtitle2 v-if="tab.isActive" :text="tab.label" />
          <BlTextBody1 v-else :text="tab.label" />
        </router-link>
      </div>

      <div
        v-if="activeTab === 'targets'"
        class="bl-session-body bg-slate-200"
        :style="{ height: 'calc(100vh - 15.5rem)' }"
      >
        <div class="bl-session-targets">
          <div class="bl-session-targets-header">
            <div class="bl-session-targets-title-container">
              <BlTextSubtitle1
                class="bl-session-targets-header-title"
                text="List of targets"
              />
              <BlBubbleCount :count="targetsFiltered.length || 0" />
            </div>
            <BlTextBody2
              class="bl-session-targets-header-desc"
              text="Here is a list of your client' targets."
            />
            <div class="grid items-center grid-cols-12 gap-2">
              <BlSingleFilter
                :class="[searchOpened ? 'col-span-6' : 'col-span-10']"
                label="Status"
                placeholder="By status"
                v-model="filterStatus"
                :options="filterStatusoOtions"
                @change="onFilterStatus"
              />
              <BlInputButton
                v-if="!searchOpened"
                class="w-full col-span-2 border-grey-200"
                icon="search"
                type="outlined"
                @clicked="searchHandler"
              />
              <BlInputSearch
                v-else
                class="col-span-6"
                :input="searchInput"
                @changed="inputHandler"
              />
            </div>
          </div>
          <div
            v-if="
              filterStatus.length === 1 && filterStatus.includes('mastered')
            "
            class="flex pl-4 pr-5 lg:pl-6 lg:pr-7"
          >
            <div class="flex items-center gap-2 p-2 rounded-b bg-info-100">
              <BlIcon icon="toast-info" class="w-5" />
              <BlTextBody2
                class="text-info"
                text="Only targets with probing can be selected for the session."
              />
            </div>
          </div>
          <div
            class="bl-session-targets-body"
            :style="{
              height:
                'calc(100vh - 4.5rem - 3rem - 4.5rem - 3rem - 1.5rem - 10rem)'
            }"
          >
            <div
              class="sticky top-0 z-10 flex-shrink-0 py-3 h-18 bg-pure-white"
            >
              <div
                class="flex items-center justify-between flex-shrink-0 h-12 gap-2 px-4 border rounded border-slate-400 bg-slate-200"
              >
                <div
                  class="flex items-center gap-2 truncate"
                  :title="`${checkedTargetIds.length} target(s) selected`"
                >
                  <BlInputCheck
                    :indeterminate="checkedTargetIds.length > 0"
                    :checked="
                      checkedTargetIds.length > 0 &&
                        checkedTargetIds.length === targetsFiltered.length
                    "
                    @changed="onCheckAllTargets()"
                  />
                  <BlTextBody2
                    :text="`${checkedTargetIds.length} target(s) selected`"
                    class="truncate"
                  />
                </div>
                <BlInputButton
                  v-if="checkedTargetIds.length"
                  :label="`Add ${checkedTargetIds.length} to session`"
                  :title="`Add ${checkedTargetIds.length} to session`"
                  icon="plus-circle"
                  type="outlined"
                  dense
                  :disabled="addMeasurementLoading"
                  @clicked="onAddCheckedTargets()"
                />
              </div>
            </div>
            <div v-if="targetsLoading" class="sticky z-20 top-18">
              <div
                class="absolute z-20 grid w-full opacity-75 -top-18 bg-pure-white place-items-center"
                :style="{
                  height:
                    'calc(100vh - 4.5rem - 3rem - 4.5rem - 3rem - 1.5rem - 10rem)'
                }"
              >
                <div class="blum-loader"></div>
              </div>
            </div>
            <div
              v-else-if="!targetsFiltered.length"
              class="flex items-center justify-center"
              :style="{
                height:
                  'calc(100vh - 4.5rem - 3rem - 4.5rem - 3rem - 1.5rem - 10rem - 4.5rem)'
              }"
            >
              <BlTextBody1 text="Target(s) not found." />
            </div>
            <div v-else class="flex flex-col gap-4">
              <div
                v-for="target in targetsFiltered"
                :key="target.id"
                class="transition-all duration-300 bl-session-target cursor-grab"
                :class="[
                  dragId === target.id.toString() ? 'opacity-0' : 'opacity-100'
                ]"
                @mousedown="dragStart"
                @touchstart="dragStart"
                :data-id="target.id"
              >
                <div
                  class="pointer-events-none bl-session-target-border"
                  :style="{
                    backgroundColor: target.curriculum_color || '#EAECF0'
                  }"
                />
                <div
                  class="whitespace-pre-line bl-session-target-body"
                  :title="target.description"
                >
                  <div class="bl-session-target-curriculum-container">
                    <div
                      class="flex items-center gap-2 truncate"
                      :title="target.curriculum_name"
                    >
                      <BlInputCheck
                        :checked="checkedTargetIds.includes(target.id)"
                        @changed="onToggleCheckTarget(target.id)"
                      />
                      <BlTextBody2
                        :text="target.curriculum_name"
                        class="truncate bl-session-target-curriculum"
                      />
                    </div>
                    <div class="flex items-center gap-2">
                      <div
                        v-if="
                          checkPassedActionRecommendation(target) &&
                            target.status !== 'mastered'
                        "
                        class="p-1 bg-orange-300"
                        :style="{ borderRadius: '4px' }"
                      >
                        <BlIcon icon="tooltip" class="w-4 h-4" />
                      </div>
                      <StatusChip :status="target.status" dense />
                    </div>
                  </div>
                  <BlTextSubtitle2
                    style="pointer-events: none;"
                    class="truncate bl-session-target-name"
                    :text="target.name"
                  />
                  <BlTextBody2
                    style="pointer-events: none;"
                    class="whitespace-pre-line bl-session-target-desc"
                    :text="target.description"
                  />
                  <div class="grid grid-cols-12 gap-2">
                    <BlInputButton
                      v-if="!measurementsFixed"
                      label="Add to fixed position"
                      title="Add to fixed position"
                      icon="lock"
                      type="outlined"
                      dense
                      class="col-span-6"
                      :disabled="addMeasurementLoading"
                      @clicked="onAddMeasurement(target.id, true)"
                    />
                    <BlInputButton
                      label="Add to session"
                      title="Add to session"
                      icon="plus-circle"
                      type="outlined"
                      dense
                      :class="[
                        measurementsFixed ? 'col-span-12' : 'col-span-6'
                      ]"
                      :disabled="addMeasurementLoading"
                      @clicked="onAddMeasurement(target.id)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bl-session-measurements">
          <MeasurementCard
            v-if="measurementsFixed"
            :measurement="measurementsFixed"
            :sessionStatus="session.status"
            class="bl-session-measurement bl-session-measurements-fixed"
          />
          <div v-else class="bl-session-measurements-fixed">
            <BlIcon
              icon="lock"
              class="bl-session-measurement-icon"
              color="primary"
            />
            <BlTextSubtitle2
              class="bl-session-measurement-name"
              text="Fixed position"
            />
            <BlTextBody2
              class="bl-session-measurement-message"
              text="Target placed here will be fixed on the top part of the page"
            />
          </div>
          <div class="bl-session-measurements-cells-container">
            <div class="bl-session-measurements-cells">
              <div
                v-for="n in measurementsSorted.length +
                  6 -
                  (measurementsSorted.length % 3)"
                :key="n"
                class="bl-session-measurement-cell"
              ></div>
            </div>
            <div
              v-if="measurementsSorted.length"
              class="bl-session-measurements-container"
            >
              <MeasurementCard
                v-for="measurement in measurementsSorted"
                :key="measurement.id"
                :measurement="measurement"
                :sessionStatus="session.status"
                class="bl-session-measurement"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="activeTab === 'comments' && session"
        class="relative flex justify-center w-screen py-6 overflow-y-auto bg-slate-200"
        :style="{ height: 'calc(100vh - 15.5rem)' }"
      >
        <SessionDraftComments :sessionId="session.id" />
      </div>

      <div
        v-if="dragTarget"
        ref="blSessionTargetDecoy"
        :style="{
          left: `${dragPos.x}px`,
          top: `${dragPos.y}px`,
          width: `${dragPos.w}px`
        }"
        class="bl-session-target-decoy"
      >
        <div
          :style="{
            backgroundColor: dragTarget.curriculum_color,
            pointerEvents: 'none'
          }"
          class="bl-session-target-border"
        ></div>
        <div class="bl-session-target-body">
          <div
            style="pointer-events: none;"
            class="bl-session-target-curriculum-container"
          >
            <div class="flex items-center gap-2 truncate">
              <BlInputCheck :checked="false" />
              <BlTextBody2
                :text="dragTarget.curriculum_name"
                class="truncate bl-session-target-curriculum"
              />
            </div>
            <div class="flex items-center gap-2">
              <div
                v-if="
                  checkPassedActionRecommendation(dragTarget) &&
                    dragTarget.status !== 'mastered'
                "
                class="p-1 bg-orange-300"
                :style="{ borderRadius: '4px' }"
              >
                <BlIcon icon="tooltip" class="w-4 h-4" />
              </div>
              <StatusChip :status="dragTarget.status" dense />
            </div>
          </div>
          <BlTextSubtitle2
            style="pointer-events: none;"
            class="truncate bl-session-target-name"
            :text="dragTarget.name"
          />
          <BlTextBody2
            style="pointer-events: none;"
            class="whitespace-pre-line bl-session-target-desc"
            :text="dragTarget.description"
          />
          <div style="pointer-events: none;" class="grid grid-cols-12 gap-2">
            <BlInputButton
              v-if="!measurementsFixed"
              label="Add to fixed position"
              icon="lock"
              type="outlined"
              dense
              class="col-span-6"
            />
            <BlInputButton
              label="Add to session"
              icon="plus-circle"
              type="outlined"
              dense
              :class="[measurementsFixed ? 'col-span-12' : 'col-span-6']"
            />
          </div>
        </div>
      </div>

      <div v-if="dragMove" class="bl-session-target-areas">
        <div
          @mouseup="onAddMeasurement(parseInt(dragId), true)"
          v-if="!measurementsFixed"
          class="bl-session-target-area-fixed"
        ></div>
        <div
          @mouseup="onAddMeasurement(parseInt(dragId), false)"
          class="bl-session-target-area"
        >
          <BlIcon
            icon="goal"
            class="bl-session-target-area-icon"
            color="primary"
          />
          <BlTextSubtitle2
            class="bl-session-target-area-name"
            text="Drop here"
          />
          <BlTextBody2
            class="bl-session-target-area-message"
            text="Target placed here will be on the last postiion"
          />
        </div>
      </div>

      <div
        class="fixed z-50 flex items-center justify-between h-16 px-6 transition-all duration-500 bg-white border rounded shadow-lg w-200 border-slate-400"
        :class="[isShowPopup ? 'bottom-16' : '-bottom-16']"
        :style="{
          left: '50%',
          transform: 'translateX(-50%)'
        }"
      >
        <div class="flex items-center gap-2">
          <BlInputButton
            type="plain"
            icon="close"
            @clicked="isShowPopup = false"
          />
          <div>
            <BlTextSubtitle3
              :text="`Quick-add targets from`"
              class="text-slate-800"
            />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <BlInputButton
            dense
            label="In acquisition status"
            :loading="addMeasurementLoading"
            @clicked="onAddAllAcquisition"
          />
        </div>
      </div>
    </div>

    <SessionNewScheduleModal
      v-if="newScheduleOpened"
      :session="session"
      @close-modal="newScheduleOpened = false"
    />

    <SessionEditScheduleModal
      v-if="editScheduleOpened"
      :session="session"
      @close-modal="editScheduleOpened = false"
    />

    <DuplicateSessionModal
      v-if="duplicateSessionOpened"
      @close-modal="duplicateSessionOpened = false"
    />

    <BlBackdrop
      v-if="downloadOpened"
      title="Download as PDF"
      description="Create an offline record of your session by generating an A4 PDF version."
      label="Download"
      class="bl-session-backdrop"
      @backdrop-close="downloadOpened = false"
      @backdrop-action="downloadSession"
    >
      <div class="px-8 bl-session-backdrop-container">
        <div class="bl-session-backdrop-preview-container">
          <BlTextBody2 text="Preview" />
          <div class="bl-session-backdrop-preview">
            <img
              class="bl-session-backdrop-preview-image"
              :src="`/static/session-template-${downloadType}.png`"
            />
            <BlTextBody3
              :text="
                downloadType === 'detailed'
                  ? '1 page can consist of up to 3 targets'
                  : '1 page can consist of up to 6 targets'
              "
              class=""
            />
          </div>
        </div>
        <div class="bl-session-backdrop-layout-container">
          <BlTextBody2
            class="bl-session-backdrop-layout-title"
            text="Choose layout"
          />
          <div
            class="bl-session-backdrop-layout"
            :class="
              downloadType === 'detailed'
                ? 'bl-session-backdrop-layout-active'
                : ''
            "
            @click="downloadType = 'detailed'"
          >
            <BlIcon
              class="bl-session-backdrop-layout-icon"
              icon="message-text"
              :color="downloadType === 'detailed' ? 'primary' : ''"
            />
            <BlTextBody2
              class="bl-session-backdrop-layout-name"
              text="Detailed template"
            />
            <BlTextBody3
              class="bl-session-backdrop-layout-description"
              text="Keep the comment boxes for a comprehensive record-keeping experience."
            />
          </div>
          <div
            class="bl-session-backdrop-layout"
            :class="
              downloadType === 'simplified'
                ? 'bl-session-backdrop-layout-active'
                : ''
            "
            @click="downloadType = 'simplified'"
          >
            <BlIcon
              class="bl-session-backdrop-layout-icon"
              icon="frame"
              :color="downloadType === 'simplified' ? 'primary' : ''"
            />
            <BlTextBody2
              class="bl-session-backdrop-layout-name"
              text="Simplified template"
            />
            <BlTextBody3
              class="bl-session-backdrop-layout-description"
              text="Without comment boxes for a cleaner and more concise layout."
            />
          </div>
        </div>
      </div>
    </BlBackdrop>

    <LaunchBeforeTheTimeModal
      v-if="launchBeforeTheTimeOpened"
      :sessionAppointment="sessionAppointment"
      @close-modal="launchBeforeTheTimeOpened = false"
      @on-submit="onLaunchSession"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";

import TargetJobProgression from "../clients/target/TargetJobProgression.vue";
import MeasurementCard from "./MeasurementCard.vue";
import StatusChip from "../clients/target/StatusChip.vue";
import SessionNewScheduleModal from "./draft/NewScheduleModal.vue";
import SessionEditScheduleModal from "./draft/EditScheduleModal.vue";
import DuplicateSessionModal from "./draft/DuplicateSessionModal.vue";
import LaunchBeforeTheTimeModal from "./draft/LaunchBeforeTheTimeModal.vue";
import SessionDraftComments from "./comment/DraftComments.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle1 from "../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlBreadcrumbs from "../BlBreadcrumbs.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlSingleFilter from "../BlSingleFilter.vue";
import BlInputButton from "../BlInputButton.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "SessionEdit",
  components: {
    TargetJobProgression,
    MeasurementCard,
    StatusChip,
    SessionNewScheduleModal,
    SessionEditScheduleModal,
    DuplicateSessionModal,
    LaunchBeforeTheTimeModal,
    SessionDraftComments,
    //
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlBubbleCount,
    BlIcon,
    BlInputButton,
    BlInputSearch,
    BlInputCheck,
    BlSingleFilter,
    BlBackdrop
  },
  data() {
    return {
      pageLoading: true,
      targetsLoading: false,
      searchOpened: false,
      saveLoading: false,
      sessionName: "Unnamed session",
      openMoreSupervisor: false,
      newScheduleOpened: false,
      editScheduleOpened: false,
      //
      duplicateSessionOpened: false,
      launchBeforeTheTimeOpened: false,
      launchLoading: false,
      downloadOpened: false,
      downloadLoading: false,
      downloadType: "detailed",
      //
      searchInput: {
        placeholder: "Search targets...",
        model: ""
      },
      filterStatus: ["in_acquisition"],
      filterStatusoOtions: [
        { value: "pending", chip: true },
        { value: "in_acquisition", chip: true },
        { value: "paused", chip: true },
        { value: "mastered", chip: true },
        { value: "discontinued", chip: true }
      ],
      searchTimeout: null,
      checkedTargetIds: [],
      measurementsFixed: null,
      measurementsSorted: [],
      targetsFiltered: [],
      addMeasurementLoading: false,
      dragX: 0,
      dragY: 0,
      dragPos: {
        x: 0,
        y: 0,
        w: 0
      },
      dragMove: false,
      dragId: "",
      dragTarget: null,
      //
      isShowPopup: false
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "client",
      "targets",
      "session",
      "measurements",
      "viewMode"
    ]),
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    breadcrumbs() {
      const currentSlug = this.$route.params.slug;
      const sessionSlug = this.session ? this.session.slug : null;

      const clientLabel = this.session
        ? this.session.client.name
        : "Client name";
      const clientTarget = this.session
        ? `/clients/${this.session.client_id}`
        : `/clients`;
      const draftTarget = this.session
        ? `/clients/${this.session.client_id}/session`
        : `/clients`;
      const currentLabel =
        currentSlug !== sessionSlug
          ? "Session name"
          : this.session.name || "Session name";
      const currentTarget = this.session
        ? `/session/${sessionSlug}/edit`
        : `/clients`;

      return [
        { label: "Clients", target: "/clients" },
        { label: clientLabel, target: clientTarget, truncate: true },
        { label: "Session Draft", target: draftTarget },
        { label: currentLabel, target: currentTarget, truncate: true }
      ];
    },
    sessionAppointment() {
      if (!this.session.appointment) {
        return {
          id: null,
          date: null,
          timeslot: "Unscheduled",
          user: null,
          supervisors: []
        };
      }

      const appointment = this.session.appointment;
      const date = moment(appointment.date).format("DD/MM/YY");
      const time = appointment.start_time_string
        ? `(${appointment.start_time_string} - ${appointment.end_time_string})`
        : "";
      const timeslot = `${date} ${time}`;

      return { ...appointment, timeslot };
    },
    appointmentSupervisors() {
      if (!this.sessionAppointment.user) return [];
      const arr = [
        this.sessionAppointment.user,
        ...this.sessionAppointment.supervisors
      ];
      return arr;
    },
    activeTab() {
      return this.$route.fullPath.includes("?tab=comments")
        ? "comments"
        : "targets";
    },
    tabs() {
      const slug = this.$route.params.slug;
      return [
        {
          target: `/session/${slug}/edit`,
          isActive: this.activeTab === "targets",
          label: "Targets"
        },
        {
          target: `/session/${slug}/edit?tab=comments`,
          isActive: this.activeTab === "comments",
          label: "Comments"
        }
      ];
    },
    searchQuery() {
      return this.searchInput.model;
    }
  },
  watch: {
    openMoreSupervisor(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMore);
        }, 10);
      }
    },
    targets(val) {
      this.generateTargetFiltered(val);
    },
    measurements: {
      deep: true,
      handler(val) {
        this.generateTargetFiltered(this.targets);
        this.measurementsSorted = [];
        this.measurementsFixed = null;
        if (val && val.length) {
          for (const measurement of val) {
            if (measurement.is_fixed) {
              this.measurementsFixed = measurement;
            } else {
              this.measurementsSorted.push(measurement);
            }
          }
          this.measurementsSorted = this.measurementsSorted.sort(
            (a, b) => a.position - b.position
          );
        }
      }
    },
    searchQuery() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(this.fetchTargets, 500);
    },
    addMeasurementLoading(val) {
      this.saveLoading = val;
    }
  },
  async mounted() {
    const slug = this.$route.params.slug;
    this.pageLoading = true;
    const { success, data: session } = await this.getSession({ slug });
    if (!success) {
      this.pageLoading = false;
      return;
    }
    if (session.status !== "draft") {
      const titleList = {
        ongoing: "The session is running",
        completed: "Session has been completed",
        cancelled: "Session has been cancelled"
      };
      const messageList = {
        ongoing:
          "This session is currently in progress, and editing is no longer possible.",
        completed:
          "This session is now finished and further edits are no longer allowed.",
        cancelled:
          "This session was cancelled by the admin and you can no longer edit it."
      };
      const title = titleList[session.status];
      const message = messageList[session.status];
      this.showToast({ title, message, type: "error" });
      this.$router.push(`/clients/${session.client_id}/session`);
      this.pageLoading = false;
      return;
    }
    this.sessionName = session.name;
    await this.fetchClient(session.client_id);
    await this.fetchTargets(session.client_id);
    const { data: measurements } = await this.getMeasurements({
      id: session.id
    });

    // check added measurments
    setTimeout(() => {
      if (!measurements || !measurements.length) {
        this.isShowPopup = true;
      }
    }, 1000);
    this.pageLoading = false;
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions([
      "getClient",
      "getSession",
      "updateSession",
      "downloadSessionTemplate",
      "getMeasurements",
      "createMeasurement",
      "addMultipleTargetsSession",
      "getTargets",
      "showToast",
      "checkTargetJob"
    ]),
    displayDate(date, format) {
      if (!date) return "";
      return moment(date).format(format);
    },
    closeHandlerMore() {
      window.removeEventListener("click", this.closeHandlerMore);
      this.openMoreSupervisor = false;
    },
    checkPassedActionRecommendation(target) {
      const arr = target.action_recommendations || [];
      const idx = arr.findIndex(i => i.visible && i.passed);
      return idx > -1;
    },
    //
    onInputName: debounce(function(e) {
      const val = e.target.value;
      this.updateName(val);
    }, 1000),
    async updateName(val) {
      if (val === this.session.name) return;
      const params = {
        id: this.session.id,
        session: { name: val }
      };
      this.saveLoading = true;
      const { success } = await this.updateSession(params);
      this.saveLoading = false;
      if (success) {
        const title = "Session updated";
        const message = "Success! The session name has been updated.";
        this.showToast({ title, message, type: "success" });
      }
    },
    async downloadSession() {
      if (!this.downloadLoading) {
        this.downloadLoading = true;
        const response = await this.downloadSessionTemplate({
          id: this.session.id,
          detailed: this.downloadType === "detailed"
        });
        const href = URL.createObjectURL(response.data);
        const link = document.createElement("a");

        let filename = "";
        let disposition = response.headers["content-disposition"];
        let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        let matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }

        link.href = href;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        this.downloadLoading = false;
      }
    },
    onStartSession() {
      if (!this.measurements.length) {
        const title = `Can't start the session with zero target!`;
        this.showToast({ title, message: "", type: "error" });
        return;
      }

      if (!this.sessionAppointment.id) {
        return this.onLaunchSession();
      }

      const t = this.sessionAppointment.start_time_string || "24:00";
      const d = `${this.sessionAppointment.date}T${t}:00`;
      const startDate = moment(d);

      let showModal = false;
      if (moment().isBefore(startDate)) {
        showModal = true;
      }

      if (showModal) this.launchBeforeTheTimeOpened = true;
      else this.onLaunchSession();
    },
    onLaunchSession() {
      this.launchBeforeTheTimeOpened = false;
      this.launchLoading = true;
      const params = {
        id: this.session.id,
        session: { status: "ongoing" }
      };
      this.updateSession(params).then(({ success, data }) => {
        this.launchLoading = false;
        if (!success) return;
        this.$router.push(`/session/${data.slug}`);
      });
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    generateTargetFiltered(val = []) {
      this.targetsFiltered = [];
      if (val && val.length) {
        let arr = val.filter(a => {
          const exist = this.measurements || [];
          return !exist.map(i => i.target_id).includes(a.id);
        });
        if (this.filterStatus.includes("mastered")) {
          arr = arr.filter(a => {
            return a.status === "mastered" ? a.probing_enable : true;
          });
        }
        arr = arr.sort((a, b) => {
          const targetA =
            this.checkPassedActionRecommendation(a) && a.status !== "mastered";
          const targetB =
            this.checkPassedActionRecommendation(b) && b.status !== "mastered";
          return targetA === targetB ? 0 : targetA ? -1 : 1;
        });
        this.targetsFiltered = arr;
      }
    },
    fetchClient(clientId) {
      this.getClient({ id: clientId }).then(({ success, data }) => {
        if (!success) return;
        if (data.running_create_target_job) {
          const check = {
            client_id: data.id,
            job_id: data.running_create_target_job.job_id
          };
          this.checkTargetJob(check);
        }
      });
    },
    async fetchTargets(clientId) {
      let params = `?client_id=${clientId || this.session.client_id}`;
      if (this.searchQuery) params += `&query=${this.searchQuery}`;
      if (this.filterStatus.length) {
        params += `&status=${this.filterStatus
          .join(",")
          .replaceAll("in_acquisition", "in_progress")}`;
      }

      this.targetsLoading = true;
      await this.getTargets({ params });
      this.targetsLoading = false;
      this.checkedTargetIds = [];
    },
    isDiff(arr, arr2) {
      if (arr.length !== arr2.length) {
        return true;
      } else {
        let diff = false;
        arr.forEach((i, idx) => {
          if (i !== arr2[idx]) diff = true;
        });
        return diff;
      }
    },
    onFilterStatus(val) {
      const isDiff = this.isDiff(this.filterStatus, val);
      this.filterStatus = val;
      if (isDiff) {
        this.searchOpened = false;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.fetchTargets, 500);
      }
    },
    searchHandler() {
      if (!this.searchOpened) {
        this.searchOpened = true;
      }
    },
    onCheckAllTargets() {
      const checkedCount = this.checkedTargetIds.length;
      const targetCount = this.targetsFiltered.length;
      if (checkedCount < targetCount) {
        this.checkedTargetIds = this.targetsFiltered.map(i => i.id);
      } else {
        this.checkedTargetIds = [];
      }
    },
    onToggleCheckTarget(id) {
      const found = this.checkedTargetIds.includes(id);
      if (found) {
        this.checkedTargetIds = this.checkedTargetIds.filter(i => i !== id);
      } else {
        this.checkedTargetIds.push(id);
      }
    },
    async onAddMeasurement(id, fixed) {
      if (this.addMeasurementLoading) return;
      this.addMeasurementLoading = true;
      let position = 1;
      let isFixed = false;
      if (fixed && !this.measurementsFixed) {
        isFixed = true;
      } else {
        position = 2;
        let index = this.measurements
          ? this.measurements.findIndex(a => a.position === position)
          : -1;
        while (index > -1) {
          position++;
          index = this.measurements.findIndex(a => a.position === position);
        }
      }
      const params = {
        id: this.session.id,
        target_id: id,
        measurement: { is_fixed: isFixed, position }
      };
      await this.createMeasurement(params);
      this.addMeasurementLoading = false;
      this.dragId = "";
      this.checkedTargetIds = [];
      this.isShowPopup = false;
    },
    async onAddCheckedTargets() {
      if (this.addMeasurementLoading) return;
      this.addMeasurementLoading = true;
      const params = {
        id: this.session.id,
        target_ids: this.checkedTargetIds
      };
      await this.addMultipleTargetsSession(params);
      this.addMeasurementLoading = false;
      this.checkedTargetIds = [];
      this.isShowPopup = false;
    },
    async onAddAllAcquisition() {
      if (this.addMeasurementLoading) return;
      this.addMeasurementLoading = true;

      // get all in acquisition targets
      this.filterStatus = [];
      let params = `?client_id=${this.session.client_id}`;

      this.targetsLoading = true;
      const { success, data } = await this.getTargets({ params });
      this.targetsLoading = false;
      if (!success) {
        this.addMeasurementLoading = false;
        return;
      }
      // asign to checked targets and add
      this.checkedTargetIds = data.targets
        .filter(i => i.status === "in_progress")
        .map(i => i.id);
      params = {
        id: this.session.id,
        target_ids: this.checkedTargetIds
      };
      await this.addMultipleTargetsSession(params);
      this.addMeasurementLoading = false;

      this.checkedTargetIds = [];
      this.isShowPopup = false;
    },
    //
    dragStart(e) {
      const target = e.target ? e.target.parentElement : null;
      if (target && this.viewMode === "desktop") {
        if (e.type === "mousedown") {
          const { x, y, width } = target.getBoundingClientRect();

          this.dragX = e.clientX;
          this.dragY = e.clientY;
          this.dragId = target.dataset.id;
          this.dragPos = { x, y, w: width };

          this.dragTarget = this.targetsFiltered.find(
            a => a.id.toString() === this.dragId
          );
          window.addEventListener("mousemove", this.drag);
          window.addEventListener("mouseup", this.dragEnd);
        } else {
          window.addEventListener("touchmove", this.drag);
          window.addEventListener("touchend", this.dragEnd);
        }
      }
    },
    drag(e) {
      this.dragMove = true;
      let dx = 0;
      let dy = 0;

      if (e.type === "mousemove") {
        dx = e.clientX - this.dragX;
        dy = e.clientY - this.dragY;
      }
      if (this.$refs.blSessionTargetDecoy) {
        this.$refs.blSessionTargetDecoy.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      }
    },
    dragEnd() {
      setTimeout(() => {
        this.dragPos = { x: 0, y: 0, w: 0 };
        this.dragTarget = null;
        this.dragMove = false;
        if (!this.addMeasurementLoading) this.dragId = "";
        window.removeEventListener("mousemove", this.drag);
        window.removeEventListener("mouseup", this.dragEnd);
        window.removeEventListener("touchmove", this.drag);
        window.removeEventListener("touchend", this.dragEnd);
      }, 10);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-session {
  position: relative;
  width: 100%;
  background-color: var(--background-depth-two-color);
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;

  * {
    user-select: none;
  }

  .bl-session-header {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    background-color: var(--background-depth-one-color);
    display: flex;
    flex-direction: column;

    .bl-session-header-detail {
      position: relative;
      width: 100%;
      height: 4.5rem;
      border-bottom: 1px solid var(--border-color);
      padding: 0 1.5rem;
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .bl-session-header-detail-container {
        position: relative;
        display: flex;
        width: 30rem;
      }

      .bl-session-header-action {
        position: relative;
        display: flex;
        gap: 1rem;
      }
    }
  }

  .bl-session-body {
    position: relative;
    width: 100%;
    padding: 1.5rem 0 0 1.5rem;
    box-sizing: border-box;
    display: flex;
    gap: 1.5rem;
    overflow-y: hidden;

    .bl-session-targets {
      position: relative;
      width: calc(1100vw / 48);
      height: calc(100%);
      background: var(--background-depth-one-color);
      border-radius: 5px;
      border: 1px solid var(--border-color);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;

      .bl-session-targets-header {
        position: relative;
        width: 100%;
        padding: 1.5rem;
        box-sizing: border-box;
        border-bottom: 1px solid var(--border-color);
        display: flex;
        flex-direction: column;

        .bl-session-targets-title-container {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .bl-session-targets-header-desc {
          position: relative;
          width: 100%;
          margin: 0.25rem 0 1rem 0;
        }
      }

      .bl-session-targets-body {
        position: relative;
        width: 100%;
        padding: 0 1.5rem 1.5rem 1.5rem;
        box-sizing: border-box;
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow-y: auto;

        .bl-session-target {
          position: relative;
          width: 100%;
          display: flex;

          .bl-session-target-border {
            position: relative;
            width: 0.375rem;
            border-top-left-radius: 0.375rem;
            border-bottom-left-radius: 0.375rem;
          }

          .bl-session-target-body {
            position: relative;
            width: calc(100% - 0.375rem);
            padding: 1rem;
            border: 1px solid var(--border-color);
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
            border-left: none;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;

            .bl-session-target-curriculum-container {
              position: relative;
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 0.5rem;
            }

            .bl-session-target-name {
              position: relative;
              margin: 0.25rem 0;
            }

            .bl-session-target-desc {
              position: relative;
              color: var(--font-secondary-color);
              margin-bottom: 1rem;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              line-clamp: 2;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
          }
        }
      }
    }

    .bl-session-measurements {
      position: absolute;
      top: 1.5rem;
      right: 0;
      width: calc(70vw + 3rem);
      height: calc(100vh - 3rem);
      box-sizing: border-box;
      display: flex;
      gap: 1.5rem;

      .bl-session-measurements-fixed {
        position: relative;
        width: calc(25% - 1.875rem);
        height: calc(65%);
        border: 1px dashed var(--primary-color);
        border-radius: 5px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .bl-session-measurement-icon {
          position: relative;
          width: 1.5rem;
          height: 1.5rem;
        }

        .bl-session-measurement-name {
          position: relative;
          margin: 0.5rem 0 0.25rem 0;
          color: var(--primary-color);
        }

        .bl-session-measurement-message {
          position: relative;
          width: 80%;
          color: var(--primary-color);
          text-align: center;
        }

        &::before {
          content: "";
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 5px;
          background: var(--primary-color);
          opacity: 0.1;
        }
      }

      .bl-session-measurement.bl-session-measurements-fixed {
        position: relative;
        width: calc(25% - 1.875rem);
        height: calc(65%);
        border: none;

        &::before {
          content: "";
          display: none;
        }
      }

      .bl-session-measurements-cells-container {
        position: absolute;
        top: -1.5rem;
        right: 0;
        width: calc(75% + 0.375rem);
        height: calc(100% + 3rem);
        padding: 1.5rem 1.5rem 1.5rem 0;
        box-sizing: border-box;
        display: flex;
        overflow-y: auto;

        .bl-session-measurements-cells {
          position: absolute;
          top: 0;
          right: 0;
          width: 100%;
          padding: 1.5rem 1.5rem 1.5rem 0;
          box-sizing: border-box;
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
          overflow-y: auto;

          &::-webkit-scrollbar {
            display: none;
          }

          .bl-session-measurement-cell {
            position: relative;
            width: calc((100% - 3rem) / 3);
            // aspect-ratio: 53 / 80;
            aspect-ratio: 100 / 160;
            border-radius: 5px;
            border: 1px dashed var(--primary-color);
            display: flex;

            &::before {
              content: "";
              position: absolute;
              width: 100%;
              height: 100%;
              border-radius: 5px;
              background: var(--primary-color);
              opacity: 0.1;
            }
          }
        }
      }

      .bl-session-measurements-container {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        padding: 1.5rem 1.5rem 1.5rem 0;
        box-sizing: border-box;
        display: flex;
        gap: 1.5rem;
        flex-wrap: wrap;
        overflow-y: auto;

        &::-webkit-scrollbar {
          display: none;
        }

        .bl-session-measurement {
          width: calc((100% - 3rem) / 3);
          // aspect-ratio: 53 / 80;
          aspect-ratio: 100 / 160;
        }
      }
    }
  }

  .bl-session-target-areas {
    z-index: 100%;
    position: fixed;
    bottom: 0;
    right: 1.25rem;
    width: calc(70vw + 3rem - 1.5rem);
    height: calc(100vh - 15.5rem);

    .bl-session-target-area-fixed {
      z-index: 100%;
      position: relative;
      width: calc(25% - 1.875rem);
      height: 100%;
      background: transparent;
    }

    .bl-session-target-area {
      z-index: 100%;
      position: absolute;
      top: 0;
      right: 0;
      width: calc(75% + 0.375rem);
      height: 100%;
      background: #f1eff6;
      border-radius: 5px;
      border: 1px dashed var(--primary-color);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      .bl-session-target-area-icon {
        position: relative;
        width: 1.5rem;
        height: 1.5rem;
      }

      .bl-session-target-area-name {
        position: relative;
        margin: 0.5rem 0 0.25rem 0;
        color: var(--primary-color);
      }

      .bl-session-target-area-message {
        position: relative;
        width: 80%;
        color: var(--primary-color);
        text-align: center;
      }
    }
  }

  .bl-session-target-decoy {
    pointer-events: none;
    z-index: 100;
    position: fixed;
    display: flex;

    .bl-session-target-border {
      position: relative;
      width: 0.375rem;
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
    }

    .bl-session-target-body {
      position: relative;
      width: calc(100% - 0.375rem);
      padding: 1rem;
      border: 1px solid var(--border-color);
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
      border-left: none;
      box-sizing: border-box;
      background: var(--background-depth-one-color);
      display: flex;
      flex-direction: column;

      .bl-session-target-curriculum-container {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
      }

      .bl-session-target-name {
        position: relative;
        margin: 0.25rem 0;
      }

      .bl-session-target-desc {
        position: relative;
        color: var(--font-secondary-color);
        margin-bottom: 1rem;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
  }

  .bl-session-backdrop {
    .bl-session-backdrop-container {
      position: relative;
      width: 100%;
      display: flex;
      gap: 1rem;

      .bl-session-backdrop-preview-container {
        position: relative;
        width: 17.5rem;
        display: flex;
        gap: 0.5rem;
        flex-direction: column;

        .bl-session-backdrop-preview {
          position: relative;
          width: 100%;
          background: #fbfbfb;
          border-radius: 0.5rem;
          border: 1px solid var(--border-color);
          padding: 1.25rem;
          box-sizing: border-box;
          display: flex;
          gap: 0.5rem;
          flex-direction: column;
          align-items: center;

          img.bl-session-backdrop-preview-image {
            position: relative;
            width: 100%;
            aspect-ratio: 1 / 1.4142;
            object-fit: cover;
          }
        }
      }

      .bl-session-backdrop-layout-container {
        position: relative;
        display: flex;
        gap: 1rem;
        flex-direction: column;

        .bl-session-backdrop-layout {
          cursor: pointer;
          position: relative;
          width: 17.5rem;
          padding: 1.25rem 3rem;
          border-radius: 0.5rem;
          border: 1px solid var(--border-color);
          box-sizing: content-box;
          display: flex;
          flex-direction: column;
          align-items: center;

          .bl-session-backdrop-layout-icon {
            pointer-events: none;
            position: relative;
            width: 1.5rem;
            height: 1.5rem;
          }

          .bl-session-backdrop-layout-name {
            pointer-events: none;
            position: relative;
            margin: 0.5rem 0 0.25rem 0;
          }

          .bl-session-backdrop-layout-description {
            pointer-events: none;
            position: relative;
            text-align: center;
          }

          &.bl-session-backdrop-layout-active {
            border-color: var(--primary-color);
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/sessions/SessionEdit.vue